import React, { useState, useEffect } from 'react';
import { Backdrop, Box, Button, CircularProgress, IconButton, Modal, Tab, TextField } from '@mui/material';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import { AnimatePresence, m } from 'framer-motion';
import { useSnackbar } from 'notistack';
import Iconify from '../../../components/Iconify';
import Deposit from './deposit/Deposit';
import Withdraw from './withdraw/Withdraw';
import useLocales from '../../../hooks/useLocales';
import { useDispatch, useSelector } from '../../../redux/store';
import { openTransactionsDialog } from '../../../redux/slices/transactionsDialog';
import CustomModal from '../../../components/CustomModal';
import { varBounce } from '../../../components/animate';
import { GoldBorderLoadingButton } from '../../../components/nav-section/vertical/style';
import useAuth from '../../../hooks/useAuth';
import conObj, { showResponseError } from '../../../utils/connection-assistant';
import { openPopupCreditReward } from '../../../redux/slices/settings';
import { getPromotions, getUserMemberBalance } from '../../../redux/slices/lookup';
import { openConfirm } from '../../../redux/slices/confirm';

const DepositWithdrawDialog = () => {
  const { isAuthenticated } = useAuth();

  const dispatch = useDispatch();

  const { translate } = useLocales();

  const { isOpenTransactionsDialog, siteSettings } = useSelector((x) => ({ ...x.transactionsDialog, ...x.lookup }));

  const [activeTab, setActiveTab] = useState('1');

  const [promoCode, setPromoCode] = useState('');

  const [isSubmitting, setIsSubmitting] = useState();

  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    setActiveTab(isOpenTransactionsDialog?.isDeposit ? '1' : '2');
  }, [openTransactionsDialog]);

  const handleChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const submitPromoCode = async () => {
    try {
      if (!isAuthenticated) return;
      if (!promoCode) throw new Error(translate('x_is_required', { x: translate('promo_code') }));
      setIsSubmitting(true);

      // DEV TESTING
      const magicCode = process.env.REACT_APP_MAGIC_CODE || '';
      if (!!magicCode && magicCode === promoCode) {
        const testResponse = await new Promise((resolve) => {
          setTimeout(() => {
            resolve({
              data: {
                data: {
                  bonusAmount: 1.65,
                },
              },
            });
          }, 1000);
        });

        dispatch(openPopupCreditReward(testResponse.data?.data?.bonusAmount));
        return;
      }

      const response = await conObj.post(`/transactions/unlock_promotion/${promoCode}.json`);

      const _bonusAmount = response.data?.data?.bonusAmount;

      if (_bonusAmount) {
        // enqueueSnackbar(translate('x_claimed', { x: `${memberBalance?.currencyCode} ${fNumber(_bonusAmount)}` }));
        dispatch(openPopupCreditReward(_bonusAmount));
      } else {
        enqueueSnackbar('bonus_unlocked');
      }

      getUserMemberBalance();

      getPromotions();

      setPromoCode('');
    } catch (error) {
      console.log(error);

      showResponseError(error, (message, option) => openConfirm({ title: 'Uh Oh!', subTitle: message, isInfo: true }));
      // showResponseError(error, enqueueSnackbar);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <CustomModal
      open={isOpenTransactionsDialog}
      onClose={() => dispatch(openTransactionsDialog({ open: false, isDeposit: true }))}
    >
      <Box sx={{ px: { xs: 2, sm: 2, md: 2, lg: 3, xl: 3 }, pb: 3, pt: 6, position: 'relative' }}>
        <TabContext value={activeTab}>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ overflowX: 'auto', maxWidth: '100%' }}>
              <TabList
                onChange={handleChange}
                aria-label="lab API tabs example"
                indicatorColor="transparent"
                TabIndicatorProps={{
                  style: { backgroundColor: 'transparent' },
                }}
                sx={{ background: '#846d34', width: 'fit-content', minHeight: 'unset', borderRadius: '15px' }}
              >
                <Tab
                  label={translate('deposit')}
                  value="1"
                  sx={{
                    background: activeTab === '1' ? '#f2c556' : '#846d34',
                    minHeight: 'unset',
                    marginRight: '10px !important',
                    p: '5px 10px',
                    fontSize: '14px',
                    borderRadius: '16px',
                    color: activeTab === '1' ? '#1b1b1b !important' : 'white',
                  }}
                />
                <Tab
                  label={translate('withdraw')}
                  value="2"
                  sx={{
                    background: activeTab === '2' ? '#f2c556' : '#846d34',
                    minHeight: 'unset',
                    p: '5px 10px',
                    fontSize: '14px',
                    borderRadius: '16px',
                    color: activeTab === '2' ? '#1b1b1b !important' : 'white',
                    marginRight: '10px !important',
                  }}
                />
                <Tab
                  label={translate('promo_code')}
                  value="3"
                  sx={{
                    background: activeTab === '3' ? '#f2c556' : '#846d34',
                    minHeight: 'unset',
                    p: '5px 10px',
                    fontSize: '14px',
                    borderRadius: '16px',
                    color: activeTab === '3' ? '#1b1b1b !important' : 'white',
                  }}
                />
              </TabList>
            </Box>
            <AnimatePresence>
              {siteSettings?.config_deposit_withdraw_phonenumber ? (
                <m.div {...varBounce().in} transition={{ delay: 2 }}>
                  {/* <IconButton
                    onClick={() =>
                      window.open(
                        `https://api.whatsapp.com/send?phone=${siteSettings?.config_deposit_withdraw_phonenumber}`,
                        '_blank'
                      )
                    }
                  >
                    <Iconify icon="ic:baseline-whatsapp" sx={{ color: 'rgb(0, 166, 80)' }} />
                  </IconButton> */}
                  <Button
                    sx={{
                      backgroundColor: 'rgb(0, 166, 80)',
                      borderRadius: '15px',
                      '&:hover': { backgroundColor: 'rgb(0, 166, 80)' },
                    }}
                    onClick={() =>
                      window.open(
                        `https://api.whatsapp.com/send?phone=${siteSettings?.config_deposit_withdraw_phonenumber}`,
                        '_blank'
                      )
                    }
                  >
                    <Iconify icon="ion:send" sx={{ color: 'white', width: 15, height: 15, mr: 1 }} />
                    <Iconify icon="ic:baseline-whatsapp" sx={{ color: 'white', width: 18, height: 18 }} />
                  </Button>
                </m.div>
              ) : (
                ''
              )}
            </AnimatePresence>
          </Box>
          <TabPanel value="1">
            <Deposit />
          </TabPanel>
          <TabPanel value="2">
            <Withdraw />
          </TabPanel>
          <TabPanel value="3">
            <Box sx={{ mb: '0', mt: '50px', width: '100%' }}>
              <Box>
                <TextField
                  fullWidth
                  InputProps={{
                    endAdornment: promoCode ? (
                      <IconButton onClick={() => setPromoCode('')} size="small">
                        <Iconify icon={'uil:times'} />
                      </IconButton>
                    ) : (
                      ''
                    ),
                    style: {
                      fontSize: 'inherit',
                    },
                  }}
                  sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' }, height: 'unset' }}
                  value={promoCode}
                  placeholder={translate('enter_promo_code')}
                  onChange={(e) => setPromoCode(e.target.value)}
                />

                {/* {formError?.userUsername && <ErrorText>{formError?.userUsername}</ErrorText>}  */}
              </Box>

              <GoldBorderLoadingButton
                fullWidth
                sx={{ whiteSpace: 'nowrap', fontSize: { xs: '0.7rem', sm: '0.8rem', md: '1rem' }, mt: 2 }}
                disabled={!promoCode || isSubmitting}
                loading={isSubmitting}
                onClick={() => submitPromoCode()}
              >
                {translate('apply_code')}
              </GoldBorderLoadingButton>
            </Box>
          </TabPanel>
        </TabContext>
      </Box>
    </CustomModal>
    //   </Box>
    // </Modal>
  );
};

export default DepositWithdrawDialog;
